import {LogHandler} from "./log-handler";
import axios from "axios";
import {Subscriber} from "rxjs";
import {PreventativeControlsListDescription} from "../utils"
import {DynamoDBClient, PutItemCommand, PutItemCommandOutput} from "@aws-sdk/client-dynamodb";
import {KinesisClient, PutRecordCommand, PutRecordCommandOutput} from "@aws-sdk/client-kinesis";
import {PutObjectCommand, PutObjectCommandOutput, S3Client} from "@aws-sdk/client-s3";
import {PublishCommand, PublishCommandOutput, SNSClient} from "@aws-sdk/client-sns";
import {SendMessageCommand, SendMessageCommandOutput, SQSClient} from "@aws-sdk/client-sqs";

type AwsCredentials = {
    AccessKeyId: string
    SecretKey: string
    SessionToken: string
    Expiration: number
}

export enum EventType {
    EVENT_TYPE_S3 = 'S3',
    EVENT_TYPE_SQS = 'SQS',
    EVENT_TYPE_SNS = 'SNS',
    EVENT_TYPE_KINESIS = 'KINESIS',
    EVENT_TYPE_DYNAMO = 'DYNAMO',
}

export type TestEvent = {
    eventType: EventType
    eventPayload: {
        success: boolean
        message: string
        description?: string
        eventType? : string
        data: any
    }
}

export class AwsTestHandler {
    public exfiltrationPayload = '{"data": "Company secrets!!!"}'
    private credentials?: AwsCredentials
    private ranTestCounter = 0
    private failedTestCounter = 0
    private logger: LogHandler
    subscriber: Subscriber<any>

    constructor(logger: LogHandler) {
        this.logger = logger
        this.subscriber = new Subscriber<any>()
    }

    private getClientConfig = () => {
        if (!this.credentials) {
            throw new Error('AWS credentials have not been initialized')
        }

        return {
            region: process.env.REACT_APP_AWS_CONFIG_REGION,
            credentials: {
                accessKeyId: this.credentials.AccessKeyId,
                secretAccessKey: this.credentials.SecretKey,
                sessionToken: this.credentials.SessionToken,
            },
            maxAttempts: 3,
            logger: this.logger,
        }
    }

    initializeTestData = (): AwsTestHandler => {
        return this
    }

    setSubscriber = (subscriber: Subscriber<any>): AwsTestHandler => {
        this.subscriber = subscriber
        return this
    }

    getRanTestsCounter = (): number => {
        return this.ranTestCounter
    }

    incrementRanTestCounter = (): AwsTestHandler => {
        this.ranTestCounter++
        return this
    }

    resetRanTestCounter = (): AwsTestHandler => {
        this.ranTestCounter = 0
        return this
    }

    getFailedTestCounter = (): number => {
        return this.failedTestCounter
    }

    incrementFailedTestCounter = (): AwsTestHandler => {
        this.failedTestCounter++
        return this
    }

    resetFailedTestCounter = (): AwsTestHandler => {
        this.failedTestCounter = 0
        return this
    }
    runAllTests = (response:any) => {
        this.credentials = response.data
        this.runS3Test()
        this.runSQSTest()
        this.runSNSTest()
        this.runKinesisTest()
        this.runDynamoDBTest()
    }

    getToken = async () => {
        const endpoint = `https://${window.location.hostname}/aws/token`
        return await axios(endpoint).then((response) => {
            return {
                data: response,
                success: true
            }
        }).catch(err => {
            return  {
                data: err,
                success: false
            }
        })        
    }

    makeTestEvent = (eventType: EventType, success: boolean, message: string,  data: any): TestEvent => {
        return {
            eventType: eventType,
            eventPayload: {
                success: success,
                message: message,
                description:PreventativeControlsListDescription[eventType].description,
                data,
                eventType
            }
        }
    }

    s3TestOnComplete = (err: Error | null, data?: PutObjectCommandOutput) => {
        const reqUrl = `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/${process.env.REACT_APP_S3_KEY}`
        const resultData = `PUT ${reqUrl} 200 OK`
        const event = this.makeTestEvent(EventType.EVENT_TYPE_S3, !!err, err ? "Error " + err : "Success " + data?.ETag, resultData)
        this.subscriber.next(event)
    }

    sqsTestOnComplete = (err: Error | null, data?: SendMessageCommandOutput) => {
        const event = this.makeTestEvent(EventType.EVENT_TYPE_SQS, !!err, err ? "Error " + err : "Success " + data?.MessageId, data)
        this.subscriber.next(event)
    }

    snsTestOnComplete = (err: Error | null, data?: PublishCommandOutput) => {
        const event = this.makeTestEvent(EventType.EVENT_TYPE_SNS, !!err, err ? "Error " + err : "Success " + data?.MessageId, data)
        this.subscriber.next(event)
    }

    kinesisTestOnComplete = (err: Error | null, data?: PutRecordCommandOutput) => {
        const event = this.makeTestEvent(EventType.EVENT_TYPE_KINESIS, !!err, err ? "Error " + err : "Success " + data?.ShardId, data)
        this.subscriber.next(event)
    }

    dynamoDBTestOnComplete = (err: Error | null, data?: PutItemCommandOutput) => {
        const event = this.makeTestEvent(EventType.EVENT_TYPE_DYNAMO, !!err, err ? "Error " + err : "Success " + data?.Attributes, data)
        this.subscriber.next(event)
    }

    runS3Test = () => {
        // Create an SQS service object
        const s3 = new S3Client(this.getClientConfig());
        const params = {
            Body: this.exfiltrationPayload,
            Bucket: process.env.REACT_APP_S3_BUCKET ?? '',
            Key: process.env.REACT_APP_S3_KEY ?? ''
        };
        s3.send(new PutObjectCommand(params))
            .then((data) => this.s3TestOnComplete(null, data))
            .catch((err) => this.s3TestOnComplete(err));
    }

    runSQSTest = () => {
        // Create an SQS service object
        const sqs = new SQSClient(this.getClientConfig());
        const params = {
            MessageBody: this.exfiltrationPayload,
            QueueUrl: process.env.REACT_APP_SQS_QUEUE_URL ?? ''
        };
        sqs.send(new SendMessageCommand(params))
            .then((data) => this.sqsTestOnComplete(null, data))
            .catch((err) => this.sqsTestOnComplete(err));
    }

    runSNSTest = () => {
        // Create publish parameters
        const params = {
            Message: this.exfiltrationPayload,
            TopicArn: process.env.REACT_APP_SNS_TOPIC_ARN ?? ''
        };
        const sns = new SNSClient(this.getClientConfig())
        sns.send(new PublishCommand(params))
            .then((data) => this.snsTestOnComplete(null, data))
            .catch((err) => this.snsTestOnComplete(err))
    }

    runKinesisTest = () => {
        // create Amazon Kinesis service object
        const kinesis = new KinesisClient(this.getClientConfig());

        // Create the Amazon Kinesis record
        const partitionKey = 'partition-' + (this.credentials?.AccessKeyId ?? 'anonymous')
        const params = {
            StreamName: process.env.REACT_APP_KINESIS_STREAM_NAME ?? '',
            Data: new TextEncoder().encode(this.exfiltrationPayload),
            PartitionKey: partitionKey
        }
        kinesis.send(new PutRecordCommand(params))
            .then((data) => this.kinesisTestOnComplete(null, data))
            .catch((err) => this.kinesisTestOnComplete(err))
    }

    runDynamoDBTest = () => {
        // Create the DynamoDB service object
        const dynamoDB = new DynamoDBClient(this.getClientConfig());
        const params = {
            TableName: process.env.REACT_APP_DYNAMODB_TABLE_NAME ?? '',
            Item: {
                'data': { S: this.exfiltrationPayload }
            },
            ReturnValues: "ALL_OLD" as const,
        };
        // Call DynamoDB to add the item to the table
        dynamoDB.send(new PutItemCommand(params))
            .then((data) => this.dynamoDBTestOnComplete(null, data))
            .catch((err) => this.dynamoDBTestOnComplete(err));
    }
}
